import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Overline } from '@/components/atoms/Eyebrow';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastInfo } from '@/components/molecules/ToastTemplate/ToastTemplate';
import {
    AllocationSummary,
    ChoiceCards,
    ConfirmRegistration,
    computeAllocationSummary,
    defaultOperationDate,
    FormSelect,
    FormTextField,
    SuggestNameButton,
    suggestFishGroupName,
    useServerErrorMessage,
} from '@/components/organisms/FishGroupForms';
import { usePermissions } from '@/contexts/auth';
import {
    type SiteTimelineScope,
    type TimelineFishGroup,
    useGetSiteTimelines,
    useSiteFishGroupActions,
} from '@/domain/fishGroups';

import {
    earliestEventDay,
    eventTimestamp,
    latestStart,
} from '../../helpers/siteActions/actionDate';
import { mergeBioscopeOutcomes } from '../../helpers/siteActions/bioscopeOutcomes';
import { currentStay, movableBioscopeIds } from '../../helpers/siteActions/groupFacts';
import {
    mergeBioscopeOptions,
    mergeBlock,
    mergeCandidates,
    mergeDestinationOptions,
    mergeStocking,
    mergeTotals,
} from '../../helpers/siteActions/mergeCandidates';
import { mergeConfirmItems } from '../../helpers/siteActions/operationConfirm';
import type { SitePen } from '../../helpers/siteActions/sitePens';
import type { SiteActionKind } from '../FishGroupDetailModal/FishGroupDetailModal';
import ModalFormActions from '../ModalFormActions/ModalFormActions';
import ActionDialogTitle from '../SiteActionDialog/ActionDialogTitle';
import EventDateField from '../SiteActionDialog/EventDateField';
import PensStatus, { pensKnown, type SitePensState } from '../SiteActionDialog/PensStatus';

import MergeCandidateList from './MergeCandidateList';
import MergeTotals from './MergeTotals';

import dialogStyles from '../SiteActionDialog/SiteActionDialog.module.scss';

interface MergeFormValues {
    date: Date | null;
    name: string;
    cageId: string;
    /** 'keep', or the source pen whose bioscopes move in. */
    bioscope: string;
}

interface MergeFishGroupModalProps {
    clientId: number;
    locationId: number;
    /** Real names: the dialog never mounts in demo mode. */
    group: TimelineFishGroup;
    pens: readonly SitePen[];
    pensState: SitePensState;
    season: string | null;
    siteNames: readonly string[];
    /** A stocking's name, for the warning when a group in none joins it. */
    cycleName: (cycleId: number) => string;
    now: Date;
    onSwitch: (kind: SiteActionKind) => void;
    onClose: () => void;
    onDone: () => void;
}

/**
 * Several groups into one pen (Figma 664:90689): every group closes and one new group is
 * created. Groups of another stocking or species can't be ticked (the backend refuses them),
 * and a merge that brings a group in no stocking into one warns before Confirm.
 */
export default function MergeFishGroupModal(props: MergeFishGroupModalProps) {
    const { clientId, locationId, group, pens, pensState, season, siteNames, cycleName, now } =
        props;
    const { onSwitch, onClose, onDone } = props;
    const { t } = useTranslation('fishGroups');
    const { canMoveBioscopes } = usePermissions();
    const [checked, setChecked] = useState<ReadonlySet<number>>(new Set([group.id]));
    const [confirming, setConfirming] = useState<MergeFormValues | null>(null);
    const actions = useSiteFishGroupActions(clientId, locationId);
    const serverError = useServerErrorMessage(actions.state.error);

    // Each occupant's details live in its own scope's timeline.
    const scopes = useMemo(() => {
        const unique = new Set<SiteTimelineScope>();
        for (const pen of pens) {
            if (pen.occupant) unique.add(pen.occupant.stockingCycleId ?? 'unassigned');
        }
        return [...unique];
    }, [pens]);
    const reads = useGetSiteTimelines(clientId, locationId, scopes);
    const { timelines } = reads;
    const candidates = useMemo(
        () => mergeCandidates(group, pens, timelines),
        [group, pens, timelines]
    );
    const selected = candidates.filter((candidate) => checked.has(candidate.fishGroupId));
    const stocking = mergeStocking(selected);
    // A ticked group that its details now block (another species, a later stocking) is dropped,
    // rather than staying ticked behind a disabled checkbox.
    const blockedIds = selected
        .filter((candidate) => mergeBlock(candidate, group, stocking, now) !== null)
        .map((candidate) => candidate.fishGroupId);
    const blockedKey = blockedIds.join(',');
    useEffect(() => {
        if (blockedKey === '') return;
        const dropped = new Set(blockedKey.split(',').map(Number));
        setChecked(
            (current) => new Set([...current].filter((fishGroupId) => !dropped.has(fishGroupId)))
        );
    }, [blockedKey]);
    const totals = mergeTotals(selected);
    // A group not loaded yet has no count to send: an unknown count is sent as none, but a
    // count still loading must not be.
    const detailsPending = selected.some((candidate) => !candidate.loaded);
    const notBefore = latestStart(
        selected.map((candidate) => (candidate.detail ? currentStay(candidate.detail)?.from : null))
    );
    const earliest = earliestEventDay(notBefore);
    const sourcePen = currentStay(group);
    const ownPen = pens.find((pen) => pen.cageId === sourcePen?.cageId);

    const { handleSubmit, control, setValue, getFieldState, formState } = useForm<MergeFormValues>({
        defaultValues: {
            date: defaultOperationDate(),
            name:
                suggestFishGroupName({
                    cageName: ownPen?.cageName,
                    operation: 'merge',
                    season,
                    existingNames: siteNames,
                }) ?? '',
            cageId: ownPen ? String(ownPen.cageId) : '',
            bioscope: 'keep',
        },
    });
    // Pens that arrive after the dialog opened seed the name and pen then, unless edited.
    const seeded = useRef(ownPen !== undefined);
    useEffect(() => {
        if (seeded.current || !ownPen) return;
        seeded.current = true;
        if (!getFieldState('cageId').isDirty) setValue('cageId', String(ownPen.cageId));
        if (!getFieldState('name').isDirty) {
            setValue(
                'name',
                suggestFishGroupName({
                    cageName: ownPen.cageName,
                    operation: 'merge',
                    season,
                    existingNames: siteNames,
                }) ?? ''
            );
        }
    }, [ownPen, season, siteNames, getFieldState, setValue]);
    const cageId = useWatch({ control, name: 'cageId' });
    const destination = pens.find((pen) => String(pen.cageId) === cageId);
    const destinationOptions = mergeDestinationOptions(pens, selected, t);
    // The bioscopes that can move in are those placed before the merge's date, not a booking
    // that starts later; the list above shows what measures each group now.
    const date = useWatch({ control, name: 'date' });
    const movableAt = (day: Date | null) => {
        const at = day ? new Date(eventTimestamp(day, notBefore)) : new Date();
        return selected.map((candidate) =>
            candidate.detail
                ? {
                      ...candidate,
                      bioscopeIds: movableBioscopeIds(currentStay(candidate.detail), at),
                  }
                : candidate
        );
    };
    const bioscopeOptions = mergeBioscopeOptions(destination, movableAt(date), t);
    const bioscope = useWatch({ control, name: 'bioscope' });
    // Unticking a group takes its pen off the destinations and its bioscope off the choices.
    const destinationOffered = destinationOptions.some((option) => option.value === cageId);
    const bioscopeOffered = bioscopeOptions.some((option) => option.value === bioscope);
    useEffect(() => {
        if (cageId !== '' && !destinationOffered) {
            setValue('cageId', ownPen ? String(ownPen.cageId) : '', { shouldValidate: true });
        }
    }, [cageId, destinationOffered, ownPen, setValue]);
    useEffect(() => {
        if (!bioscopeOffered) setValue('bioscope', 'keep');
    }, [bioscopeOffered, setValue]);
    const submitting = formState.isSubmitting || actions.state.isLoading;
    const mayMoveBioscopes = canMoveBioscopes(clientId, locationId);
    // Customers never create a group outside a stocking (Mauro, 2026-10-20).
    const resultInNoStocking = stocking === null;
    const unstocked = selected.filter((candidate) => candidate.stockingCycleId === null);

    const toggle = (fishGroupId: number, on: boolean) =>
        setChecked((current) => {
            const next = new Set(current);
            if (on) next.add(fishGroupId);
            else next.delete(fishGroupId);
            return next;
        });

    const close = () => {
        if (submitting) return;
        actions.state.reset();
        onClose();
    };

    const register = async (values: MergeFormValues) => {
        if (!values.date || detailsPending) return;
        const target = Number(values.cageId);
        // Only from a ticked group's pen into the destination: the backend refuses any other.
        const moving =
            values.bioscope === 'keep'
                ? []
                : (movableAt(values.date).find(
                      (candidate) =>
                          String(candidate.pen.cageId) === values.bioscope &&
                          candidate.pen.cageId !== target
                  )?.bioscopeIds ?? []);
        const done = await actions
            .merge({
                sources: selected.map((candidate) => ({
                    fishGroupId: candidate.fishGroupId,
                    fishCount: candidate.fishCount,
                })),
                date: eventTimestamp(values.date, notBefore),
                name: values.name,
                cageId: target,
                bioscopeMoves: moving.map((bioscopeId) => ({ bioscopeId, targetCageId: target })),
            })
            .then(
                () => true,
                () => false
            );
        if (!done) return;
        ToastInfo({
            content: t('siteActions.merge.successToast', {
                name: values.name.trim(),
                count: selected.length,
            }),
            status: 'success',
            icon: 'success',
            options: { toastId: 'site-fish-group-merge-success', autoClose: 5000 },
        });
        actions.state.reset();
        onDone();
    };

    if (confirming?.date) {
        const toPen = destination?.displayName ?? '';
        const emptied = selected
            .map((candidate) => candidate.pen)
            .filter((pen) => pen.cageId !== destination?.cageId)
            .map((pen) => pen.displayName);
        // The same move the request sends: a ticked pen's bioscopes into the destination.
        const movingFrom =
            confirming.bioscope === 'keep'
                ? undefined
                : movableAt(confirming.date).find(
                      (c) =>
                          String(c.pen.cageId) === confirming.bioscope &&
                          c.pen.cageId !== destination?.cageId
                  );
        const bioscopes = mergeBioscopeOutcomes({
            destination,
            sourcePens: selected.map((candidate) => candidate.pen),
            moving: movingFrom
                ? { ids: movingFrom.bioscopeIds, fromCageId: movingFrom.pen.cageId }
                : null,
        });
        return (
            <ModalComponent
                isOpen
                size="lg"
                onClose={close}
                aria-label={t('siteActions.confirm.title')}
                backdrop="static"
                body={
                    <ConfirmRegistration
                        fromPens={selected.map((candidate) => candidate.pen.displayName)}
                        toPens={[toPen]}
                        title={t('siteActions.confirm.title')}
                        items={mergeConfirmItems(
                            {
                                date: confirming.date,
                                name: confirming.name.trim(),
                                groups: selected.length,
                                bioscopes,
                                emptiedPens: emptied,
                            },
                            t
                        )}
                        warning={
                            unstocked.length > 0 && stocking !== null
                                ? t('siteActions.merge.joinsStocking', {
                                      count: unstocked.length,
                                      names: unstocked.map((c) => c.name).join(', '),
                                      stocking: cycleName(stocking),
                                  })
                                : undefined
                        }
                        error={
                            serverError && <p className={dialogStyles.serverError}>{serverError}</p>
                        }
                        cancelLabel={t('actions.cancel')}
                        confirmLabel={t('siteActions.confirm.confirm')}
                        isSubmitting={submitting || detailsPending}
                        onCancel={() => {
                            actions.state.reset();
                            setConfirming(null);
                        }}
                        onConfirm={() => register(confirming)}
                    />
                }
            />
        );
    }

    const body = (
        <form
            className={dialogStyles.form}
            onSubmit={handleSubmit((values) => setConfirming(values))}>
            <EventDateField control={control} name="date" earliest={earliest} />
            <AllocationSummary
                summary={computeAllocationSummary({
                    sources: selected.map((candidate) => ({ fishGroupId: candidate.fishGroupId })),
                    destinations: [{ estimatedFishCount: String(totals.fishCount ?? '') }],
                    populationById: new Map(
                        selected.map((candidate) => [
                            candidate.fishGroupId,
                            {
                                fishGroupId: candidate.fishGroupId,
                                initialPopulationSize: candidate.fishCount,
                            },
                        ])
                    ),
                })}
                sources={[]}
                populationById={new Map()}
                titleKey="actions.operate.allocation.combinedSourcesTitle"
                disclaimerKey="siteActions.split.poolBasis"
            />
            <section className={dialogStyles.section}>
                <Overline as="h3" className={dialogStyles.sectionLabel}>
                    {t('siteActions.merge.groupsToMerge')}
                </Overline>
                <PensStatus state={pensState} />
                {reads.error && (
                    <Alert
                        tone="danger"
                        size="small"
                        action={
                            <Button variant="tertiary" size="small" onClick={reads.refetch}>
                                {t('newFishGroup.retryPens')}
                            </Button>
                        }>
                        {t('siteActions.merge.detailsError')}
                    </Alert>
                )}
                <MergeCandidateList
                    candidates={candidates}
                    checked={checked}
                    blockOf={(candidate) => mergeBlock(candidate, group, stocking, now)}
                    onToggle={toggle}
                />
                <Alert tone="info" size="small">
                    {t('siteActions.merge.emptyPens')}{' '}
                    <Button
                        variant="link"
                        className={dialogStyles.inlineLink}
                        onClick={() => onSwitch('move')}>
                        {t('siteActions.merge.useMove')}
                    </Button>
                    .
                </Alert>
            </section>
            <section className={dialogStyles.section}>
                <Overline as="h3" className={dialogStyles.sectionLabel}>
                    {t('siteActions.merge.whereFishEndUp')}
                </Overline>
                <div className={dialogStyles.twoColumns}>
                    <FormTextField
                        control={control}
                        name="name"
                        label={t('siteActions.split.name')}
                        rules={{
                            validate: (value) => typeof value === 'string' && value.trim() !== '',
                        }}
                        labelAction={
                            <SuggestNameButton
                                cageName={destination?.cageName}
                                operation="merge"
                                season={season}
                                existingNames={siteNames}
                                onApply={(name) => setValue('name', name, { shouldValidate: true })}
                            />
                        }
                    />
                    <FormSelect
                        control={control}
                        name="cageId"
                        label={t('siteActions.split.pen')}
                        placeholder={t('siteActions.split.penPlaceholder')}
                        options={destinationOptions}
                        rules={{ required: true }}
                    />
                </div>
                <MergeTotals
                    fishCount={totals.fishCount}
                    grams={totals.grams}
                    loading={detailsPending && reads.error === null}
                />
            </section>
            {mayMoveBioscopes && bioscopeOptions.length > 1 && (
                <section className={dialogStyles.section}>
                    <Overline as="h3" className={dialogStyles.sectionLabel}>
                        {t('siteActions.form.bioscope')}
                    </Overline>
                    <Controller
                        control={control}
                        name="bioscope"
                        render={({ field }) => (
                            <ChoiceCards
                                aria-label={t('siteActions.form.bioscope')}
                                value={field.value}
                                onChange={field.onChange}
                                options={bioscopeOptions}
                            />
                        )}
                    />
                    <p className={dialogStyles.hint}>
                        {t('siteActions.merge.bioscopesStay', {
                            pen: destination?.displayName ?? '',
                        })}
                    </p>
                </section>
            )}
            {selected.length < 2 && (
                <Alert tone="neutral" size="small">
                    {t('siteActions.merge.pickAnother')}
                </Alert>
            )}
            {selected.length >= 2 && resultInNoStocking && (
                <Alert tone="warning" size="small">
                    {t('siteActions.merge.needsStocking')}
                </Alert>
            )}
            {serverError && <p className={dialogStyles.serverError}>{serverError}</p>}
            <ModalFormActions
                cancelLabel={t('actions.cancel')}
                submitLabel={t('siteActions.merge.submit')}
                isSubmitting={submitting}
                submitDisabled={
                    selected.length < 2 ||
                    resultInNoStocking ||
                    !pensKnown(pensState) ||
                    detailsPending
                }
                onCancel={close}
            />
        </form>
    );

    return (
        <ModalComponent
            isOpen
            size="lg"
            onClose={close}
            title={
                <ActionDialogTitle
                    kind="merge"
                    title={t('siteActions.merge.title')}
                    groupName={group.name}
                    penName={sourcePen?.cageName ?? null}
                    bioscopeIds={
                        candidates.find((candidate) => candidate.initial)?.bioscopeIds ?? []
                    }
                />
            }
            body={body}
            backdrop="static"
        />
    );
}
